import { Avatar, Chip, IconButton, Tooltip } from "@mui/material";
import {
  Apartment as ApartmentIcon,
  BookmarkBorderOutlined,
  Check as CheckIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  KeyboardReturnRounded as KeyboardReturnRoundedIcon,
  Lock as LockIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  VisibilityOutlined as VisibilityIcon,
  PhoneForwardedOutlined as PhoneForwardedOutlinedIcon,
} from "@mui/icons-material";
import cuid from "cuid";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";

import {
  changePatient,
  selectedReport,
  setSelectedRow,
  toggleModal,
} from "app/store/transmission/slice/transmissionSlice";
import { AlertIcon } from "components/AlertIcon";
import {
  EVOKE_ROLES,
  USER_ROLES,
  BENCHMARK_BILLING_CODES,
  CLINIC_ROLES,
  EVOKE_ACTIVE,
  CLINIC_ACTIVE,
  CLINIC_FOR_REVIEW,
} from "static/constants";
import { formatDateTime, formatLocalDateTime } from "utils/date";
import formatDate from "utils/formatDate";
import { checkHasMultipleRpmManufacturers } from "utils/rpmDevice.util";
import { truncate } from "utils/string";
import "../TransmissionTable.css";
import {
  checkHasHospitalColumnAccess,
  checkIsHospitalRestrictedUser,
} from "utils/transmissionTable";
import HospitalColumnFilter from "components/common/filters/hospital/HospitalColumnFilter";
import {
  filterNonUCHAccounts,
  checkReportAssignmentAccess,
} from "utils/account.util";
import { textFilter } from "utils/table";
import { capitalize } from "lodash";
import { useSelector } from "react-redux";
import EHRStatus from "components/common/ehr_status/EHRStatus";
import useDashboardReportPresence from "./useDashboardReportPresence";
import { generateAvatar } from "app/utils/avatar";

const useTransmissionTableColumn = (view) => {
  const role = localStorage.getItem("role");
  const userRole = localStorage.getItem("userRole");
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);
  const { selectedTab, reportList } = useSelector(
    (state) => state.transmission
  );

  const filteredAccounts = filterNonUCHAccounts(currentUser?.accounts);
  const hasReportAssignmentAccess = checkReportAssignmentAccess(currentUser);

  const ehrFilterSelectOptions = [
    { label: "All", value: "" },
    { label: "Completed", value: "completed" },
    { label: "Pending", value: "pending" },
    { label: "Error", value: "error" },
    { label: "Timeout", value: "timeout" },
    { label: "Not Initiated", value: "not_initiated" },
  ];

  const handleNextSendClick = async (rowData) => {
    dispatch(
      changePatient({
        patientId: rowData?.patient?._id,
        serialNumber: rowData?.patient_serial_num,
        account: rowData?.account,
      })
    );
    dispatch(toggleModal({ type: "nextSend", value: true }));
  };

  const showHospitalColumn = checkHasHospitalColumnAccess(
    currentUser,
    userRole
  );

  // Only a physician is restricted, so only a physician gets the tickable list. Anyone
  // else keeps the free-text match the server expects from them.
  const isHospitalRestricted = checkIsHospitalRestrictedUser(
    currentUser,
    userRole
  );

  const reportIdsKey = reportList?.map((report) => report._id).join(",") || "";
  const reportIds = useMemo(
    () => (reportIdsKey ? reportIdsKey.split(",") : []),
    [reportIdsKey]
  );
  const presenceMap = useDashboardReportPresence(reportIds);

  let columns = useMemo(
    () => [
      {
        header: "",
        // Shown in the column picker: flags who else is viewing the report.
        columnLabel: "Report Viewers",
        enableSorting: false,
        accessorKey: "visibility",
        enableColumnFilter: false,
        enableGlobalFilter: false,
        size: 50,
        Cell: ({ row }) => {
          const reportId = row?.original?._id;
          const activeUsersInReport =
            presenceMap?.[reportId]?.filter(
              (user) => user?.userId !== currentUser?.userId
            ) || [];

          if (activeUsersInReport.length === 0) return null;

          return (
            <Tooltip
              placement="bottom"
              slotProps={{
                tooltip: {
                  sx: {
                    minWidth: "168px",
                    padding: "8px 12px",
                  },
                },
              }}
              title={
                <div>
                  {activeUsersInReport.map((user, index) => (
                    <div key={index} className="d-flex align-items-center mb-1">
                      <Avatar
                        sx={{
                          backgroundColor: "#eaf2ff",
                          color: "#175ec8",
                          height: "30px",
                          width: "30px",
                          marginRight: "8px",
                          fontSize: "12px",
                        }}
                        alt={user?.name}
                      >
                        {generateAvatar(user?.name)}
                      </Avatar>
                      {user?.name}
                    </div>
                  ))}
                </div>
              }
            >
              <Chip
                size="small"
                icon={<VisibilityIcon fontSize="small" />}
                label={activeUsersInReport.length}
                sx={{
                  backgroundColor: "#EAF2FF",
                  color: "#175EC8",
                  "& .MuiChip-icon": {
                    color: "#175EC8",
                    marginLeft: "4px",
                    marginRight: "-8px",
                  },
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "date",
        header: "Date",
        filterVariant: "date",
        enableHiding: true,
        filterFn: textFilter,
        enableMultiSort: true,
        accessorFn: (rowData) => formatDate(new Date(rowData?.report_date)),
        Cell: ({ cell }) => cell.getValue(),
      }, //0
      {
        header: "Patient Name",
        accessorKey: "name",
        filterVariant: "text",
        size: 195,
        enableHiding: true,
        filterFn: textFilter,
        enableMultiSort: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const {
            transmissionAlertInformation: {
              alert_priority,
              alert_description,
            } = {},
            account,
            billing_code,
            billable,
            patient: { information: { idtf = null } = {} } = {},
          } = rowData || {};

          return (
            <div
              className="d-flex transmission_patient_column"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(
                  changePatient({
                    patientId: rowData?.patient?._id,
                    serialNumber: rowData?.patient_serial_num,
                    account: rowData?.account,
                  })
                );
                dispatch(
                  selectedReport({
                    id: rowData?._id,
                    isLegacyReport: rowData?.legacy ?? true,
                    reportDetail: rowData,
                  })
                );
                dispatch(toggleModal({ type: "patient", value: true }));
              }}
            >
              <div className="transmission_type_section">
                <Tooltip
                  title={
                    <div className="transmision_name_tooltip_content">
                      <span
                        className="transmision_name_tooltip_text"
                        style={{ fontSize: "12px", lineHeight: "14px" }}
                      >
                        {rowData?.name}
                        {rowData?.transmission_type === "inOffice" &&
                          "(In-Office)"}
                      </span>
                      <span className="transmision_name_tooltip_text">
                        Click to view patient profile
                      </span>
                    </div>
                  }
                  placement="bottom"
                >
                  <div className="trans-table__patient-link">
                    <span>{truncate(rowData?.name, 18)}</span>
                    {rowData?.transmission_type === "inOffice" && (
                      <IconButton className="icon_button">
                        <ApartmentIcon
                          className="icon"
                          color="info"
                          fontSize="small"
                        />
                      </IconButton>
                    )}
                    {rowData?.transmission_type === "connectivityStatus" && (
                      <IconButton className="icon_button">
                        <PhoneForwardedOutlinedIcon
                          className="icon"
                          color="info"
                          fontSize="small"
                        />
                      </IconButton>
                    )}
                  </div>
                </Tooltip>
              </div>
              {alert_priority && alert_description && (
                <Tooltip
                  title={
                    <>
                      <div style={{ fontWeight: "bold" }}>
                        Priority: {alert_priority}
                      </div>
                      <div
                        style={{ marginTop: "10px", whiteSpace: "pre-line" }}
                      >
                        {alert_description.replaceAll(/\\n/g, " ")}
                      </div>
                    </>
                  }
                  placement="bottom"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {alert_priority === "HIGH" || alert_priority === "LOW" ? (
                    <ErrorIcon
                      className="error"
                      color={alert_priority === "HIGH" ? "error" : "primary"}
                      style={{ marginLeft: "10px" }}
                      fontSize="small"
                    />
                  ) : alert_priority === "MEDIUM" ||
                    alert_priority === "NONE" ? (
                    <WarningIcon
                      style={{ marginLeft: "10px" }}
                      fontSize="small"
                      color={alert_priority === "MEDIUM" ? "warning" : "action"}
                    />
                  ) : (
                    <></>
                  )}
                </Tooltip>
              )}
              {selectedTab === "active" &&
                accountsMapper[account]?.settings?.export_for_billing &&
                accountsMapper[account]?.settings?.billing_type === "SPLIT" &&
                idtf &&
                BENCHMARK_BILLING_CODES.includes(billing_code) &&
                billable && (
                  <Tooltip
                    title={
                      <>
                        <div>To be sent to Benchmark</div>
                      </>
                    }
                    placement="bottom"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "10px" }}
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11 2L12 2.975L13 2V13.05C13 14.129 12.1067 15 11 15H3C1.89333 15 1 14.129 1 13.05V11.1H3V2L4 2.975L5 2L6 2.975L7 2L8 2.975L9 2L10 2.975L11 2ZM2.33333 13.05C2.33333 13.4075 2.63333 13.7 3 13.7H9V12.4H2.33333V13.05ZM11 13.7C11.3667 13.7 11.6667 13.4075 11.6667 13.05V3.95H4.33333V11.1H10.3333V13.05C10.3333 13.4075 10.6333 13.7 11 13.7Z"
                        fill="#4CAF50"
                      />
                      <path
                        d="M8.13089 7.1021C7.23324 6.86459 6.94457 6.61903 6.94457 6.2366C6.94457 5.79781 7.34396 5.49186 8.01226 5.49186C8.71615 5.49186 8.97714 5.83404 9.00087 6.33724H9.8748C9.84711 5.64483 9.4319 5.00879 8.60542 4.80348V3.92188H7.4191V4.79141C6.65194 4.96048 6.03505 5.46771 6.03505 6.24465C6.03505 7.17457 6.79034 7.63751 7.89363 7.90723C8.88223 8.14876 9.07996 8.50302 9.07996 8.8774C9.07996 9.15516 8.88619 9.59798 8.01226 9.59798C7.19765 9.59798 6.87734 9.22763 6.83384 8.7526H5.96387C6.01132 9.63421 6.65985 10.1294 7.4191 10.2944V11.168H8.60542V10.3025C9.37654 10.1535 9.98947 9.69862 9.98947 8.87337C9.98947 7.7301 9.02855 7.33962 8.13089 7.1021Z"
                        fill="#4CAF50"
                      />
                    </svg>
                  </Tooltip>
                )}
            </div>
          );
        },
      }, //1
      {
        header: "Alert",
        accessorKey: "device_alert",
        filterVariant: "checkbox",
        enableSorting: false,
        enableColumnFilter: false,
        size: 60,
        Cell: ({ row }) => {
          const { deviceInformation, patientAlertInformation } =
            row?.original?.patient || {};

          return (
            <AlertIcon
              device_alert={deviceInformation?.alertDevice}
              device_alert_text={deviceInformation?.alertDeviceText}
              patient_alert={patientAlertInformation?.alertPatient}
              patient_alert_text={patientAlertInformation?.alertPatientText}
            />
          );
        },
      },
      {
        header: "MRN",
        accessorKey: "mrn",
        filterVariant: "text",
        filterFn: textFilter,
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return (
            <Tooltip
              title={rowData?.patient?.mrn}
              placement="bottom"
              sx={{ whiteSpace: "pre-line" }}
            >
              <div
                style={{
                  width: "80px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {rowData?.patient?.mrn || "-"}
              </div>
            </Tooltip>
          );
        },
      }, //2
      {
        header: "Manufacturer",
        accessorKey: "manufacturer",
        filterFn: textFilter,
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const manufacturers = [
            ...new Set([
              rowData?.manufacturer,
              ...(rowData?.additionalManufacturer || []),
            ]),
          ];

          const hasMultipleRpmManufacturers =
            checkHasMultipleRpmManufacturers(manufacturers);

          return hasMultipleRpmManufacturers ? (
            <Tooltip
              title={
                <ul className="trans-table__alertList">
                  {manufacturers?.map((manufacturer) => (
                    <li key={manufacturer}>{manufacturer}</li>
                  ))}
                </ul>
              }
              placement="bottom"
            >
              <p>Evoke RPM</p>
            </Tooltip>
          ) : (
            <p>{rowData?.manufacturer}</p>
          );
        },
      }, //3
      {
        accessorKey: "hospital",
        header: "Hospital",
        ...(isHospitalRestricted
          ? { Filter: HospitalColumnFilter }
          : { filterVariant: "text", filterFn: textFilter }),
        enableSorting: false,
        enableHiding: !showHospitalColumn ? true : undefined,
        Cell: ({ row }) => row?.original?.patient?.hospital,
      }, //4
      {
        header: "Device Category",
        accessorKey: "deviceType",
        filterVariant: "text",
        enableSorting: false,
        enableHiding: true,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const deviceType = rowData?.device_category;

          return deviceType || "";
        },
      },
      {
        header: "Report Notes",
        accessorKey: "reportNotes",
        filterVariant: "text",
        filterFn: textFilter,
        size: 170,
        enableMultiSort: true,
        enableHiding: true,
        Cell: ({ row }) => {
          const rowData = row?.original;
          let alertHtml = null;
          const reportNotes = rowData?.reportNotes?.evoke_notes;

          const evoke_notes = Array.isArray(reportNotes)
            ? reportNotes?.filter(
                (item) => item?.content?.length > 0 && item?.note?.length > 0
              )
            : [];

          if (Array.isArray(rowData?.alert)) {
            alertHtml = (
              <ul className="trans-table__alertList">
                {rowData?.alert?.map((item) => (
                  <li key={cuid()}>{item}</li>
                ))}
              </ul>
            );
          } else {
            alertHtml = <div>{rowData?.alert}</div>;
          }

          const html = (
            <div style={{ whiteSpace: "pre-line", margin: "10px" }}>
              <div style={{ fontWeight: "bold" }}>Evoke Notes</div>
              {evoke_notes?.map((item) => {
                return (
                  <div key={cuid()}>
                    <div style={{ marginTop: "10px" }}>{item?.note}:</div>
                    <ul
                      className="trans-table__alertList"
                      style={{ marginLeft: "16px" }}
                    >
                      {item?.content?.map((content) => {
                        return <li key={cuid()}>{content?.description}</li>;
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          );

          const clinicHtml = (
            <div style={{ margin: "10px" }}>
              {rowData?.clinic_notes?.length !== 0 && (
                <div style={{ whiteSpace: "pre-line" }}>
                  {rowData?.account} Notes - {rowData?.clinic_notes}
                </div>
              )}
              {evoke_notes?.length > 0 && (
                <div style={{ whiteSpace: "pre-line", marginTop: "15px" }}>
                  <div style={{ fontWeight: "bold" }}>Evoke Notes:-</div>
                  {evoke_notes?.map((item) => {
                    return (
                      <div key={cuid()}>
                        <div style={{ marginTop: "10px" }}>{item?.note}:</div>
                        <ul
                          className="trans-table__alertList"
                          style={{ marginLeft: "16px" }}
                        >
                          {item?.content?.map((content) => {
                            return <li key={cuid()}>{content?.description}</li>;
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          const generalColumn = (
            <Tooltip
              title={evoke_notes?.length > 0 ? html : alertHtml}
              placement="bottom"
            >
              <div
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                }}
                className="d-flex align-items-center"
              >
                <div
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    lineHeight: "20px",
                  }}
                >
                  {evoke_notes?.length > 0 ? (
                    evoke_notes
                      ?.map((item) =>
                        item?.content?.map((content) => content?.description)
                      )
                      ?.join(", ")
                  ) : Array.isArray(rowData?.alert) ? (
                    <span>{rowData?.alert[0]}</span>
                  ) : (
                    <span>{rowData?.alert}</span>
                  )}
                </div>

                {EVOKE_ROLES.includes(role) &&
                  selectedTab === EVOKE_ACTIVE &&
                  reportNotes?.length > 0 && (
                    <BookmarkBorderOutlined
                      style={{
                        color: "#175EC8",
                        height: "18px",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    />
                  )}
              </div>
            </Tooltip>
          );

          const clinicColumn = (
            <Tooltip
              style={{ fontSize: "18px" }}
              title={
                rowData?.clinic_notes?.length || evoke_notes?.length
                  ? clinicHtml
                  : alertHtml
              }
              placement="bottom"
            >
              <div
                style={{
                  width: "80px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {rowData?.clinic_notes?.length !== 0 && (
                  <span>{rowData?.clinic_notes}</span>
                )}
                {rowData?.clinic_notes?.length === 0 &&
                  evoke_notes?.length !== 0 && (
                    <span>
                      {evoke_notes
                        ?.map((item) =>
                          item?.content?.map((content) => content?.description)
                        )
                        ?.join(", ")}
                    </span>
                  )}
                {rowData?.clinic_notes?.length === 0 &&
                evoke_notes?.length === 0 &&
                Array.isArray(rowData?.alert) ? (
                  <span>{rowData?.alert[0]}</span>
                ) : (
                  <span>{rowData?.alert}</span>
                )}
              </div>
            </Tooltip>
          );

          const renderedColumn =
            role === "clinic" ? clinicColumn : generalColumn;
          return renderedColumn;
        },
      }, // 5
      {
        header: "Billable",
        accessorKey: "billable",
        enableSorting: false,
        size: 95,
        filterFn: textFilter,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Yes", value: "checked" },
          { label: "No", value: "unchecked" },
        ],
        Cell: ({ cell }) => (cell?.getValue() ? <CheckIcon /> : <RemoveIcon />),
      }, // 7
      {
        header: "Last Billing",
        accessorKey: "lastBillDate",
        filterVariant: "date",
        filterFn: textFilter,
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return rowData?.patient?.last_bill_date ? (
            <div className="trans-table__editDate">
              <span>
                {formatDateTime(rowData?.patient?.last_bill_date, "MM/DD/YY")}
              </span>
            </div>
          ) : (
            <span>-</span>
          );
        },
      },
      {
        header: "Next Send",
        accessorKey: "nextSend",
        filterVariant: "date",
        enableSorting: false,
        filterFn: textFilter,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const isCrossFacilityReport =
            rowData?.cross_facility_report && CLINIC_ROLES.includes(userRole);

          const nextSend = formatDateTime(
            rowData?.patient?.next_send,
            "MM/DD/YY"
          );
          return (
            <div className="trans-table__editDate">
              {rowData?.patient?.next_send ? (
                <span>{nextSend}</span>
              ) : (
                <span>-</span>
              )}
              {!isCrossFacilityReport && (
                <span
                  className="trans-table__editDate-icon"
                  onClick={async (event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    handleNextSendClick(rowData);
                  }}
                >
                  <EditIcon fontSize="small" />
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Account",
        accessorKey: "account",
        filterVariant: "text",
        filterFn: textFilter,
        size: 200,
        enableMultiSort: true,
        enableHiding: filteredAccounts.length <= 1 ? true : undefined,
        Cell: ({ row }) => {
          const rowData = row?.original;

          return (
            <div
              className="trans-table__account"
              style={{ position: "relative" }}
            >
              {rowData.locked && (
                <div style={{ position: "absolute", right: "0", top: "0" }}>
                  <Tooltip title="Report locked" placement="bottom">
                    <LockIcon style={{ color: "#e30000", opacity: ".6" }} />
                  </Tooltip>
                </div>
              )}
              {rowData.account}
            </div>
          );
        },
      },
      {
        header: "Assigned Physician",
        accessorKey: "assignedPhysician",
        filterVariant: "text",
        filterFn: textFilter,
        enableSorting: role === "clinic",
        enableMultiSort: false,
        size: 180,
        Cell: ({ row }) => {
          const rowData = row?.original;

          const isPhysician = userRole === USER_ROLES.PHYSICIAN;
          const isAllowedTab = [
            EVOKE_ACTIVE,
            CLINIC_ACTIVE,
            CLINIC_FOR_REVIEW,
          ].includes(selectedTab);

          const hasAutoAssigningPhysician =
            CLINIC_ROLES.includes(userRole) && hasReportAssignmentAccess;

          const canAssignPhysician =
            !isPhysician && isAllowedTab && !hasAutoAssigningPhysician;

          const assignedPhysician = rowData?.assignedUser?.name;

          return (
            <div className="physician-edit-container">
              {assignedPhysician && <span>{assignedPhysician}</span>}

              {canAssignPhysician && (
                <span
                  className="physician-edit"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(toggleModal({ type: "physician", value: true }));
                    dispatch(setSelectedRow(rowData));
                  }}
                >
                  {assignedPhysician ? (
                    <EditIcon fontSize="small" />
                  ) : (
                    "+ Assign Physician"
                  )}
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "OAC",
        accessorKey: "oac",
        filterVariant: "select",
        size: 100,
        filterFn: textFilter,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
          { label: "Unknown", value: "unknown" },
        ],
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;

          return capitalize(
            rowData?.patient?.information?.anticoag_information?.anticoag_status
          );
        },
      },
      {
        header: "Report status",
        accessorKey: "returned",
        filterVariant: "select",
        size: 140,
        filterFn: textFilter,
        enableHiding: !CLINIC_ROLES.includes(userRole) ? true : undefined,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Returned", value: "checked" },
        ],
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          if (view === "clinic") {
            const ReportColumn = (
              <Tooltip
                style={{ fontSize: "18px", width: "300px" }}
                title={"This report has been unprocessed"}
                placement="bottom"
              >
                <span style={{ marginLeft: "20px" }}>
                  <KeyboardReturnRoundedIcon />
                </span>
              </Tooltip>
            );
            if (rowData?.returned) {
              return ReportColumn;
            }
          }
        },
      },
      {
        header: "Clinic Billed",
        accessorKey: "clinic_billed",
        filterVariant: "checkbox",
        enableSorting: false,
        enableColumnFilter: false,
        filterFn: textFilter,
        enableHiding: !CLINIC_ROLES.includes(userRole) ? true : undefined,
        Cell: ({ cell }) => (cell?.getValue() ? <CheckIcon /> : <RemoveIcon />),
      }, //12
      {
        size: 130,
        header: "ORU",
        accessorKey: "oru",
        enableSorting: false,
        filterVariant: "select",
        enableColumnFilter: true,
        filterSelectOptions: ehrFilterSelectOptions,
        cellStyle: {
          textAlign: "center",
        },
        Cell: ({ row }) => {
          const rowData = row?.original;
          const reportAccount = rowData?.account;

          const oruConfig =
            accountsMapper[reportAccount]?.settings?.hl7_configs?.oru;
          const isNotApplicableStatus =
            !oruConfig ||
            (!rowData?.last_billing_date &&
              !rowData?.alert_flag &&
              !rowData?.warning_flag);

          const oruStatus = isNotApplicableStatus
            ? "not_applicable"
            : rowData?.oru_status;

          return <EHRStatus status={oruStatus} />;
        },
      },
      {
        size: 130,
        header: "DFT",
        accessorKey: "dft",
        enableSorting: false,
        filterVariant: "select",
        enableColumnFilter: true,
        filterSelectOptions: ehrFilterSelectOptions,
        cellStyle: {
          textAlign: "center",
        },
        Cell: ({ row }) => {
          const rowData = row?.original;
          const reportAccount = rowData?.account;

          const dftConfig =
            accountsMapper[reportAccount]?.settings?.hl7_configs?.dft;
          const isNotApplicableStatus =
            !dftConfig || !rowData?.last_billing_date;

          const dftStatus = isNotApplicableStatus
            ? "not_applicable"
            : rowData?.dft_status;

          return <EHRStatus status={dftStatus} />;
        },
      },
      {
        header: "Signature",
        accessorKey: "eSig",
        filterVariant: "select",
        size: 105,
        enableSorting: false,
        filterFn: textFilter,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Yes", value: "checked" },
          { label: "No", value: "unchecked" },
        ],
        Cell: ({ cell }) => (cell?.getValue() ? <CheckIcon /> : <RemoveIcon />),
      }, // 15
      {
        header: "Signed By",
        accessorKey: "signedBy",
        filterVariant: "text",
        enableSorting: false,
        filterFn: textFilter,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return rowData?.signedBy?.name || <span>-</span>;
        },
      },
      {
        accessorKey: "signedAt",
        header: "Signed At",
        filterVariant: "date",
        filterFn: textFilter,
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const signedAt = formatLocalDateTime(rowData?.signed_at, "MM/DD/YY");
          return (
            <div className="trans-table__editDate">
              {rowData?.signed_at ? <span>{signedAt}</span> : <span>-</span>}
            </div>
          );
        },
      },
    ],
    [
      accountsMapper,
      showHospitalColumn,
      isHospitalRestricted,
      currentUser?.name,
      filteredAccounts.length,
      hasReportAssignmentAccess,
      presenceMap,
      role,
      selectedTab,
      userRole,
      view,
    ]
  );

  return { columns };
};

export default useTransmissionTableColumn;
